import { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faTimes,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import { DateTime } from "luxon"
import Loader from "../../components/Loader"
import { useVideoLogById } from "../../hooks/useVideoLogs"

const GENERIC_VIDEO = "GENERIC_VIDEO"

const formatLabel = (value) => (value || "").replace(/_/g, " ")

const formatCreatedDate = (value) => {
  if (!value) return "—"
  const dt =
    value instanceof Date
      ? DateTime.fromJSDate(value)
      : DateTime.fromISO(String(value))
  return dt.isValid ? dt.toFormat("MMM d, yyyy HH:mm") : "—"
}

const parseTrace = (raw) => {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const humanizeStep = (value) =>
  (value || "")
    .split("_")
    .map((word) => (word ? word[0] + word.slice(1).toLowerCase() : word))
    .join(" ")

const ResolutionTrace = ({ trace }) => {
  const steps = parseTrace(trace)

  if (!steps.length)
    return <p className="text-sm text-gray-400">No trace recorded.</p>

  return (
    <ol className="flex flex-col">
      {steps.map((step, stepIndex) => {
        const isLast = stepIndex === steps.length - 1
        const dotClass =
          step.result === "FOUND"
            ? "bg-green-500"
            : step.result === "NOT_FOUND"
              ? "bg-red-500"
              : "bg-blue-500"
        return (
          <li key={stepIndex} className="relative pl-8 pb-4 last:pb-0">
            {!isLast && (
              <span className="absolute left-[11px] top-7 bottom-0 w-0.5 bg-gray-100" />
            )}
            <span
              className={`absolute left-0 top-1 flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold text-white ring-4 ring-white ${dotClass}`}
            >
              {stepIndex + 1}
            </span>
            <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold text-gray-800">
                  {humanizeStep(step.step)}
                </span>
                {step.result ? (
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                      step.result === "FOUND"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {step.result}
                  </span>
                ) : null}
              </div>
              {step.query ? (
                <div className="mt-1 text-xs text-gray-500 break-words">
                  <span className="font-medium text-gray-600">query:</span>{" "}
                  {step.query}
                </div>
              ) : null}
              {Array.isArray(step.npis) && step.npis.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {step.npis.map((npi) => (
                    <span
                      key={npi}
                      className="font-mono text-[11px] rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-gray-600"
                    >
                      {npi}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

/*
  The trace is fetched per report when the drawer opens rather than shipped
  with the list: reading every row's trace made the list endpoint ~10s slower.
*/
const TraceDrawer = ({ record, onClose }) => {
  const { data: detail, isLoading } = useVideoLogById(record?.reportId)

  if (!record) return null
  const resolutionMethod = detail?.ResolutionMethod
  const resolutionTrace = detail?.ResolutionTrace
  const steps = parseTrace(resolutionTrace)
  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm"
        role="presentation"
        onClick={onClose}
      />
      <div className="fixed right-0 top-0 bottom-0 z-50 flex w-[460px] max-w-[92vw] flex-col overflow-hidden rounded-l-2xl bg-white shadow-2xl ring-1 ring-black/5">
        <div className="relative bg-gradient-to-br from-blue-600 to-indigo-600 px-5 py-5 text-white">
          <button
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 transition-colors"
            title="Close"
            type="button"
            onClick={onClose}
          >
            <FontAwesomeIcon className="w-3.5 h-3.5" icon={faTimes} />
          </button>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-blue-100">
            Accession
          </div>
          <div className="font-mono text-lg font-bold tracking-tight">
            {record.accessionNo}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {resolutionMethod && resolutionMethod !== GENERIC_VIDEO ? (
              <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-semibold">
                {resolutionMethod}
              </span>
            ) : null}
            <span className="text-xs text-blue-100">
              {formatCreatedDate(record.createdDate)}
            </span>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              Resolution trace
            </p>
            {!isLoading && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
                {steps.length} step{steps.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center gap-3 py-10">
              <Loader />
              <span className="text-sm text-gray-600">Loading trace...</span>
            </div>
          ) : (
            <ResolutionTrace trace={resolutionTrace} />
          )}
        </div>
      </div>
    </>
  )
}

const RefCard = ({ group, onOpenTrace }) => {
  const accessions = group.accessions || []
  const name =
    [group.firstName, group.lastName].filter(Boolean).join(" ") ||
    group.refName ||
    "Unknown"

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-3">
      <div className="text-sm font-semibold text-gray-900 truncate">{name}</div>

      {accessions.length ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          {accessions.map((accession) => (
            <button
              key={accession.reportId}
              className="font-mono text-sm font-semibold text-blue-600 hover:underline"
              type="button"
              onClick={() => onOpenTrace(accession)}
            >
              {accession.accessionNo}
            </button>
          ))}
        </div>
      ) : (
        <span className="mt-1.5 block text-xs text-gray-400">No records</span>
      )}
    </div>
  )
}

const NotFoundPhysicianList = ({
  groups,
  total,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  sentinelRef,
}) => {
  const [traceRecord, setTraceRecord] = useState(null)

  return (
    <div className="flex flex-col">
      <div className="flex justify-between items-center mb-2 px-1">
        <h2 className="text-sm font-semibold text-gray-700">
          {formatLabel("NOT_FOUND")}
          <span className="ml-2 font-normal text-xs text-gray-500">
            {total.toLocaleString()} referring physician
            {total === 1 ? "" : "s"}
          </span>
        </h2>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20 gap-4">
          <Loader />
          <span className="text-gray-600">Loading referring physicians...</span>
        </div>
      ) : groups.length > 0 ? (
        <div className="flex flex-col gap-2 max-h-[calc(100vh-390px)] overflow-auto pr-1">
          {groups.map((group, index) => (
            <RefCard
              key={`${group.refName || "unknown"}-${index}`}
              group={group}
              onOpenTrace={setTraceRecord}
            />
          ))}

          <div ref={sentinelRef} className="h-1" />

          {hasNextPage && (
            <div className="flex justify-center py-4">
              {isFetchingNextPage ? (
                <div className="flex items-center gap-2">
                  <Loader />
                  <span className="text-gray-600">Loading more records...</span>
                </div>
              ) : (
                <button
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors text-sm font-medium"
                  onClick={onLoadMore}
                >
                  Load More
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 py-16 bg-white border border-gray-200 rounded-xl">
          <FontAwesomeIcon
            className="w-8 h-8 text-gray-300"
            icon={faTriangleExclamation}
          />
          <p className="text-sm text-gray-500">No records for these filters.</p>
        </div>
      )}

      <TraceDrawer record={traceRecord} onClose={() => setTraceRecord(null)} />
    </div>
  )
}

export default NotFoundPhysicianList
